import { BaseEntity } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { IzzyChannelMessageEntity } from "../generated/entity_subclasses.js";

/**
 * Client-safe extended ChannelMessageEntity with business logic methods.
 *
 * All methods set fields only — they do NOT call Save().
 * The caller decides when to persist changes.
 *
 * For server-side use with email sending, reprocessing triggers, etc.,
 * the ChannelMessageEntityExtended in IzzyLib extends this class and
 * registers with higher priority (3).
 */
@RegisterClass(BaseEntity, 'Channel Messages')
export class ChannelMessageEntityExtendedClient extends IzzyChannelMessageEntity {

    // --- Approval Transitions ---

    /**
     * Whether this message may transition to Approved right now:
     * - Pending or Escalated
     * - generation settled (not Read / Creating Reply)
     * - has reply content to send (GeneratedReplyContent or ApprovedReplyContent)
     * - Failed/Skipped generations require manually edited content
     *
     * Approving sends the reply immediately, so EVERY approve affordance
     * (button, keyboard shortcut, swipe, bulk) must defer to this — a
     * disabled button alone is not a guard.
     */
    get IsApprovable(): boolean {
        return ChannelMessageEntityExtendedClient.IsApprovableState(this);
    }

    /**
     * Field-level form of IsApprovable for plain data rows (e.g. the approval queue's list
     * items, which are Picks over the entity, not instances). Same rule, one definition —
     * so list-level affordances like the thread "Approve N pending" button can't drift from
     * the commit-point guard. Field types derive from the entity (typescript-style rule:
     * never hand-copy a value-list union).
     */
    static IsApprovableState(m: Pick<IzzyChannelMessageEntity,
        'ApprovalStatus' | 'GenerationStatus' | 'GeneratedReplyContent' | 'ApprovedReplyContent'>): boolean {
        if (m.ApprovalStatus !== 'Pending' && m.ApprovalStatus !== 'Escalated') return false;
        if (m.GenerationStatus === 'Read' || m.GenerationStatus === 'Creating Reply') return false;
        const hasReplyContent = !!m.GeneratedReplyContent || !!m.ApprovedReplyContent;
        if (!hasReplyContent) return false;
        if (m.GenerationStatus === 'Failed' || m.GenerationStatus === 'Skipped') {
            return !!m.ApprovedReplyContent;
        }
        return true;
    }

    /** Whether this message may transition to Rejected: Pending/Escalated with generation settled. */
    get IsRejectable(): boolean {
        if (this.ApprovalStatus !== 'Pending' && this.ApprovalStatus !== 'Escalated') return false;
        return this.GenerationStatus !== 'Read' && this.GenerationStatus !== 'Creating Reply';
    }

    /**
     * Whether this message may transition to Escalated: never from Approved (approving sends
     * immediately, so an Approved message has usually already gone out — escalating it re-arms
     * IsApprovable, and a second approval re-enters the send path: a duplicate reply to the
     * member). An Approved-but-unsent message (failed send) goes through Re-open instead.
     * EVERY escalate path defers to this — the approval queue's buttons and ticket-level
     * escalation alike (TicketState.PickEscalationTarget only returns escalatable carriers,
     * so a ticket whose replies are all Approved cannot be escalated). The server enforces
     * the same rule on the Approved→Escalated edge and independently refuses to re-send an
     * already-sent message, so no future caller can recreate the double-send.
     */
    get IsEscalatable(): boolean {
        return this.ApprovalStatus !== 'Approved' && this.ApprovalStatus !== 'Escalated';
    }

    Approve(feedback?: string): void {
        this.ApprovalStatus = 'Approved';
        this.ApprovedAt = new Date();
        if (feedback !== undefined) {
            this.ApprovalFeedback = feedback;
        }
    }

    Reject(feedback?: string): void {
        this.ApprovalStatus = 'Rejected';
        this.ApprovedAt = new Date();
        if (feedback !== undefined) {
            this.ApprovalFeedback = feedback;
        }
    }

    Escalate(feedback?: string): void {
        this.ApprovalStatus = 'Escalated';
        this.EscalatedAt = new Date();
        if (feedback !== undefined) {
            this.ApprovalFeedback = feedback;
        }
        // Attribution (EscalatedByPersonID) is stamped SERVER-side from the request's context
        // user when the escalation saves — deliberately not set here, so no caller can spoof it
        // and no UI has to remember it.
    }

    MarkAsNotRequired(reason: string): void {
        this.SkipCategory = 'other';
        this.SkipReasoning = reason;
        this.ApprovalStatus = 'Not Required';
        this.ApprovedAt = new Date();
    }

    ApproveWithEdits(content: string, isManualWrite: boolean, feedback?: string): void {
        this.ApprovedReplyContent = ChannelMessageEntityExtendedClient.NormalizeEditorHtml(content);
        this.ApprovalStatus = 'Approved';
        this.ApprovedAt = new Date();
        if (isManualWrite) {
            this.GenerationStatus = 'Manual';
        }
        if (feedback !== undefined) {
            this.ApprovalFeedback = feedback;
        }
    }

    // --- Reply Recipients (CC / reply-all) ---

    /**
     * Records the approver's chosen CC list for the reply BEFORE the approving Save().
     * The server send path (sendApprovedMessage) reads SentCCRecipients as explicit human
     * intent — it still applies suppression and the recipient cap, then overwrites the field
     * with the post-suppression list actually sent. Pass null/empty for sender-only.
     */
    SetPlannedCCRecipients(cc: string[] | null): void {
        const cleaned = (cc ?? []).map(a => a.trim()).filter(a => a.length > 0);
        this.SentCCRecipients = cleaned.length > 0 ? cleaned.join(', ') : null;
    }

    /**
     * The reply-all CC prefill for composers: everyone on the inbound message (To + CC as
     * captured) except the channel's own mailbox and the sender, case-insensitively deduped in
     * original order. DISPLAY prefill only — the server re-applies full suppression (other
     * monitored mailboxes, noreply patterns, the cap) at send time.
     */
    GetReplyAllPrefill(channelExternalIdentifier: string | null | undefined): string[] {
        const split = (v: string | null) => (v ?? '').split(',').map(a => a.trim()).filter(a => a.length > 0);
        const exclude = new Set<string>();
        if (channelExternalIdentifier) exclude.add(channelExternalIdentifier.trim().toLowerCase());
        if (this.Sender) exclude.add(this.Sender.trim().toLowerCase());
        const out: string[] = [];
        for (const address of [...split(this.ToRecipients), ...split(this.CCRecipients)]) {
            const key = address.toLowerCase();
            if (exclude.has(key)) continue;
            exclude.add(key);
            out.push(address);
        }
        return out;
    }

    /**
     * True when a composer Cc string covers this message's whole reply-all prefill (see
     * GetReplyAllPrefill) — case-insensitive, same normalization; extra addresses are allowed.
     * Reply All means "everyone on the thread, plus whoever else the approver adds" (the composer
     * keeps the Reply All mode when the list is extended in-session), so a composer restoring a
     * persisted Cc lights the toggle whenever the prefill is fully present, without
     * re-implementing the prefill's address rules.
     */
    IsReplyAllCC(cc: string | null, channelExternalIdentifier: string | null | undefined): boolean {
        const prefill = this.GetReplyAllPrefill(channelExternalIdentifier);
        if (prefill.length === 0) return false;
        const have = new Set((cc ?? '').split(',').map(a => a.trim().toLowerCase()).filter(a => a.length > 0));
        return prefill.every(a => have.has(a.toLowerCase()));
    }

    // --- Quality Feedback ---

    SubmitQualityFeedback(rating: 'Up' | 'Down' | null, contactId: string): void {
        if (rating === null || this.QualityFeedback === rating) {
            // Toggle off
            this.QualityFeedback = null;
            this.QualityFeedbackAt = null;
            this.QualityFeedbackByPersonID = null;
        } else {
            this.QualityFeedback = rating;
            this.QualityFeedbackAt = new Date();
            this.QualityFeedbackByPersonID = contactId;
        }
    }

    // --- Staff-originated rows (Manual Messages, issue #289) ---

    /**
     * True for rows created by staff via Create Manual Message ('New' = compose,
     * 'Reply' = follow-up) — non-Forward OutboundType with NO inbound half. AI replies only
     * receive OutboundType at send time and always carry the inbound MessageContent.
     * These rows have nothing for the AI to (re)generate: every reprocess/force-generation
     * path is a no-op for them.
     */
    get IsStaffOriginated(): boolean {
        return !!this.OutboundType && this.OutboundType !== 'Forward' && !this.MessageContent;
    }

    /**
     * The reply editor's baseline — what Revert restores and what the Changes tab diffs
     * against: the AI draft when one exists, otherwise the last saved content. Staff-originated
     * rows (and Failed/Skipped generations edited by hand) have no AI draft; binding the
     * baseline to GeneratedReplyContent alone gave them an empty baseline, so Revert wiped the
     * whole message and the Revert button was armed the moment any text existed (bug found
     * 2026-09-14, shipped with Manual Messages in PR #305). EVERY editor affordance defers to
     * this — the approval queue and the ticket view's inline editor alike.
     */
    get EditBaselineContent(): string {
        return this.GeneratedReplyContent || this.ApprovedReplyContent || '';
    }

    // --- Reprocessing ---

    ResetForReprocessing(preserveEscalatedStatus: boolean = false): void {
        if (this.IsStaffOriginated) return; // no inbound body — nothing to reprocess
        this.GenerationStatus = 'Read';
        this.ErrorType = null;
        this.ErrorMessage = null;
        this.GeneratedReplyContent = null;
        this.GeneratedAt = null;
        this.AIAgentRunID = null;
        this.AIConfidenceScore = null;
        this.AIConfidenceReason = null;
        this.AIEvaluatedAt = null;
        this.AIAutoApproved = null;
        this.SkipCategory = null;
        this.SkipReasoning = null;
        if (preserveEscalatedStatus && this.ApprovalStatus === 'Escalated') {
            // Keep Escalated status for forced generations
        } else {
            this.ApprovalStatus = 'Pending';
        }
    }

    // --- Assignment ---

    AssignTo(contactId: string): void {
        this.Set('AssignedToPersonID', contactId);
        this.Set('AssignedAt', new Date().toISOString());
    }

    Unassign(): void {
        this.Set('AssignedToPersonID', null);
        this.Set('AssignedAt', null);
    }

    // --- Generation ---

    ForceAIGeneration(): void {
        if (this.IsStaffOriginated) return; // no inbound body — nothing to generate from
        this.GenerationStatus = 'Read';
        this.ForcedGeneration = true;
    }

    // --- Rescore ---

    TriggerRescore(): void {
        this.AIConfidenceScore = -1; // sentinel detected by server Save() override
        this.AIConfidenceReason = null;
        this.AIEvaluatedAt = null;
    }

    CanRescore(): boolean {
        if (!this.GeneratedReplyContent) return false;
        if (!this.AIAgentRunID) return false;
        if (this.GenerationStatus === 'Creating Reply') return false;
        return true;
    }

    // --- Reopen ---

    ReopenForReview(): void {
        // Staff drafts reopen as-is: their content is human-authored (ApprovedReplyContent,
        // GenerationStatus='Manual') — there is nothing for the AI to regenerate.
        const needsReprocessing = !this.IsStaffOriginated &&
                                  (this.ApprovalStatus === 'Not Required' ||
                                   this.ApprovalStatus === 'Rejected');

        this.ApprovalStatus = 'Pending';
        this.ApprovedAt = null;
        // Reopening voids the previous disposition entirely: if the AI auto-approved it,
        // that decision no longer stands — a later HUMAN approval must not be mislabeled
        // as auto-approved (and must get approval attribution + auto-assignment, which
        // the server-side guard skips when AIAutoApproved is still truthy).
        this.AIAutoApproved = null;

        if (needsReprocessing) {
            this.GenerationStatus = 'Read';
            this.GeneratedReplyContent = null;
            this.SkipCategory = null;
            this.SkipReasoning = null;
        }
    }

    // --- Retry Send ---

    RetrySend(): void {
        this.ErrorType = null;
        this.ErrorMessage = null;
    }

    CanRetrySend(): boolean {
        if (!this.ErrorType || this.SentContent) return false;
        const nonRetryableErrors = ['AgentFailure', 'Other'];
        return !nonRetryableErrors.includes(this.ErrorType);
    }

    // --- Reprocess Eligibility ---

    CanReprocess(): boolean {
        if (this.IsStaffOriginated) return false; // no inbound body — nothing to reprocess
        const status = this.GenerationStatus;
        return status === 'Failed' || status === 'Creating Reply';
    }

    // --- Format Detection ---

    /**
     * Reverses the Quill 2.0.3 getSemanticHTML() bug that emits every space inside a text run
     * as &nbsp; (upstream main still does this; no release since 2.0.3). Mail clients cannot
     * wrap on non-breaking spaces, so a paragraph saved that way renders as one unbreakable word
     * and breaks mid-letter at the window edge. Isolated &nbsp; (and the literal U+00A0 form)
     * become ordinary spaces; runs of two or more are kept because they carry intent
     * (indentation, deliberate double spacing) and would collapse in HTML otherwise.
     */
    static NormalizeEditorHtml(html: string | null | undefined): string {
        if (!html) return '';
        return html
            .replace(/(?<!&nbsp;)&nbsp;(?!&nbsp;)/g, ' ')
            .replace(/(?<!\u00a0)\u00a0(?!\u00a0)/g, ' ');
    }

    static DetectMessageFormat(body: string | null | undefined): 'HTML' | 'PlainText' | 'Markdown' {
        if (!body) {
            return 'PlainText';
        }

        const htmlPatterns = [
            /<html[\s>]/i, /<body[\s>]/i, /<div[\s>]/i, /<p[\s>]/i,
            /<br\s*\/?>/i, /<span[\s>]/i, /<table[\s>]/i, /<a\s+href/i,
            /<img\s+/i, /<strong[\s>]/i, /<em[\s>]/i, /<b[\s>]/i,
            /<i[\s>]/i, /<ul[\s>]/i, /<ol[\s>]/i, /<li[\s>]/i,
            /<h[1-6][\s>]/i, /&nbsp;/i, /&lt;/i, /&gt;/i, /&amp;/i
        ];

        for (const pattern of htmlPatterns) {
            if (pattern.test(body)) {
                return 'HTML';
            }
        }

        const markdownPatterns = [
            /^#{1,6}\s+/m, /\*\*[^*]+\*\*/, /__[^_]+__/,
            /\*[^*]+\*/, /_[^_]+_/, /\[.+\]\(.+\)/,
            /^>\s+/m, /^[-*+]\s+/m, /^\d+\.\s+/m,
            /```[\s\S]*```/, /`[^`]+`/
        ];

        let markdownMatchCount = 0;
        for (const pattern of markdownPatterns) {
            if (pattern.test(body)) {
                markdownMatchCount++;
            }
        }

        if (markdownMatchCount >= 2) {
            return 'Markdown';
        }

        return 'PlainText';
    }

    // --- Draft ---

    SaveDraftContent(content: string, isManualWrite: boolean): void {
        this.ApprovedReplyContent = ChannelMessageEntityExtendedClient.NormalizeEditorHtml(content);
        if (isManualWrite) {
            this.GenerationStatus = 'Manual';
        }
    }
}

export function LoadChannelMessageEntityExtended(): void {}
